/**
 * DecoderBrief — shared renderer for an Opportunity Decoder result.
 * Used on /challenge (fresh decode) and /decoded/:token (public share card).
 * Credit-score-style: core problem stays as the hero hook; the rest are
 * scannable badge rows that open a full detail view.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircleQuestion, ArrowRight } from "lucide-react";
import { ScoreRow, ScoreDetail } from "./reports/ScoreAnalysis";

const NAVY = "#0F1B2D";
const ORANGE = "#FF6A00";
const YELLOW = "#FFC145";

export default function DecoderBrief({ result, ctaTo = "/early-access", ctaLabel = "Share your story · Join early access", ctaTestId = "decoder-cta-early-access" }) {
  const [sel, setSel] = useState(null);

  const sections = [
    {
      id: "skills",
      badge: "Top Skills",
      color: "#4CAF50",
      title: "Top skills they emphasize",
      preview: (result.top_skills || []).slice(0, 4).join(" · "),
      body: (
        <div className="flex flex-wrap gap-2" data-testid="decoder-skills">
          {(result.top_skills || []).map((s, i) => (
            <span key={s} className="px-3 py-1 rounded-full border-2 text-xs font-bold"
                  style={{ borderColor: NAVY, background: i < 3 ? YELLOW : "white", color: NAVY }}>
              {s}
            </span>
          ))}
        </div>
      ),
    },
    {
      id: "summary",
      badge: "Strategy",
      color: "#1E40AF",
      title: "Strategic summary",
      preview: result.strategic_summary,
      body: (
        <p className="text-sm leading-relaxed" style={{ color: "#334155" }} data-testid="decoder-summary">
          {result.strategic_summary}
        </p>
      ),
    },
    {
      id: "questions",
      badge: "Ask These",
      color: "#7C3AED",
      title: "Three questions to ask in the interview",
      preview: (result.interview_questions || [])[0],
      body: (
        <ol className="space-y-3" data-testid="decoder-questions">
          {(result.interview_questions || []).map((q) => (
            <li key={q} className="flex gap-2 text-sm" style={{ color: "#334155" }}>
              <MessageCircleQuestion size={15} className="flex-shrink-0 mt-0.5" style={{ color: ORANGE }} />
              {q}
            </li>
          ))}
        </ol>
      ),
    },
  ];
  const open = sel != null ? sections[sel] : null;

  return (
    <div className="mt-5 space-y-4" data-testid="decoder-result">
      <div className="border-2 p-4" style={{ borderColor: NAVY, background: "#FFF7ED" }}>
        <div className="font-mono-rm text-[10px] uppercase tracking-[2px] font-bold mb-1" style={{ color: ORANGE }}>
          · The core problem they're hiring to solve
        </div>
        <p className="text-sm font-bold" style={{ color: NAVY }} data-testid="decoder-core-problem">{result.core_problem}</p>
      </div>

      <div className="space-y-2">
        {sections.map((s, i) => (
          <ScoreRow
            key={s.id}
            label={s.badge}
            color={s.color}
            title={s.title}
            description={s.preview}
            onClick={() => setSel(i)}
            testId={`decoder-row-${s.id}`}
          />
        ))}
      </div>

      <ScoreDetail
        open={open != null}
        onClose={() => setSel(null)}
        label={open?.badge}
        color={open?.color}
        eyebrow="Opportunity decoder"
        title={open?.title || ""}
        testId="decoder-detail"
      >
        {open?.body}
      </ScoreDetail>

      <div className="border-t-2 border-dashed pt-4 text-center" style={{ borderColor: "#E2E8F0" }}>
        <p className="text-sm font-bold" style={{ color: NAVY }}>That took under a minute. Imagine what a full RESMO briefing does.</p>
        <Link to={ctaTo}
              className="mt-3 inline-flex items-center gap-2 px-6 py-3 border-2 font-black text-xs uppercase tracking-[1.5px]"
              style={{ background: ORANGE, borderColor: NAVY, color: "white", boxShadow: `3px 3px 0 0 ${NAVY}` }}
              data-testid={ctaTestId}>
          {ctaLabel} <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}
